import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Zap, Award, Users, Headphones, Star } from 'lucide-react';
import { services } from '../data/mock';
import { getMachines, getTestimonials } from '../services/apiService';
import SEO from '../components/SEO';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Home = () => {
  const { settings } = useSiteSettings();
  const [machines, setMachines] = useState({ co2: [], fiber: [], mixed: [] });
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all machines
        const allMachines = await getMachines();
        
        // Group machines by type
        const groupedMachines = {
          co2: allMachines.filter(m => m.type === 'co2'),
          fiber: allMachines.filter(m => m.type === 'fiber'),
          mixed: allMachines.filter(m => m.type === 'mixed')
        };
        
        setMachines(groupedMachines);
        
        // Fetch testimonials
        const testimonialsData = await getTestimonials();
        setTestimonials(testimonialsData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="AKM Laser - Machines laser professionnelles CO2, Fibre & Mixtes"
        description="Spécialiste des machines de gravure et découpe laser pour artisans et industriels. Accompagnement, formation et support inclus. Découvrez nos machines CO2, Fibre et Mixtes."
        keywords="machine laser, gravure laser, découpe laser, laser CO2, laser fibre, machine gravure professionnelle, AKM Laser"
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={settings.heroImage}
            alt="Machine laser gravure et découpe AKM Laser CO2"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Machines Laser<br />
              <span style={{ color: '#D4C4A8' }}>
                Professionnelles
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed">
              Spécialistes de la vente de machines laser CO2, fibre et mixtes.<br />
              Accompagnement complet : conseil, formation, installation et SAV réactif.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold text-white shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-lg"
                style={{ backgroundColor: '#5C3A21' }}
              >
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/machines"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white/10 backdrop-blur-sm text-white font-semibold border-2 border-white/30 hover:bg-white/20 transition-all duration-300 text-lg"
              >
                Découvrir nos machines
              </Link>
            </div>
            <div className="flex flex-wrap gap-6">
              {[
                { icon: Award, text: 'Depuis 2015' },
                { icon: CheckCircle, text: 'Formation incluse' },
                { icon: Headphones, text: 'SAV réactif' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center space-x-2" style={{ color: '#F5EBDD' }}>
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Pourquoi choisir AKM Laser ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Beaucoup de nos clients viennent après un mauvais achat en ligne.<br />
              Avec AKM Laser, vous avez un vrai accompagnement humain et professionnel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: 'Accompagnement complet',
                description: 'Conseil personnalisé pour choisir LA machine adaptée à vos besoins réels'
              },
              {
                icon: Award,
                title: 'Machines sélectionnées',
                description: 'Cahier des charges strict : nous ne vendons que des machines testées et fiables'
              },
              {
                icon: Zap,
                title: 'Formation incluse',
                description: 'Prise en main complète de la machine et du logiciel. Vous repartez autonome'
              },
              {
                icon: Headphones,
                title: 'SAV réel et rapide',
                description: 'Support technique par téléphone, email ou à distance. Nous sommes là pour vous'
              }
            ].map((feature, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-gradient-to-br from-[#F5EBDD]/30 to-white border hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 group"
                style={{ borderColor: '#F5EBDD' }}
              >
                <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#F5EBDD' }}>
                  <feature.icon className="w-8 h-8" style={{ color: '#5C3A21' }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#5C3A21' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Machine Categories Preview */}
      <section className="py-24" style={{ backgroundColor: '#F5EBDD' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Nos Machines Laser
            </h2>
            <p className="text-xl text-gray-700">
              Découvrez notre gamme complète de machines laser professionnelles
            </p>
            <div className="mt-6 inline-flex items-center px-6 py-3 rounded-xl bg-white border-2" style={{ borderColor: '#5C3A21' }}>
              <Zap className="w-5 h-5 mr-2" style={{ color: '#5C3A21' }} />
              <p className="font-semibold" style={{ color: '#5C3A21' }}>
                Installation sur ligne de fabrication possible sur les machines fibre
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                type: 'CO2',
                title: 'Laser CO2',
                description: 'Gravure et découpe de matériaux non métalliques',
                materials: 'Bois, acrylique, cuir, tissu, papier',
                image: machines.co2[0]?.image || 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600',
                link: '/machines?type=co2'
              },
              {
                type: 'Fibre',
                title: 'Laser Fibre',
                description: 'Découpe métal industrielle haute performance',
                materials: 'Acier, inox, aluminium, cuivre',
                image: machines.fiber[0]?.image || 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600',
                link: '/machines?type=fiber'
              },
              {
                type: 'Mixte',
                title: 'Machines Mixtes',
                description: 'Double technologie CO2 + Fibre',
                materials: 'Tous matériaux : métal et non-métal',
                image: machines.mixed[0]?.image || 'https://images.unsplash.com/photo-1581092335397-9583eb92d232?w=600',
                link: '/machines?type=mixed'
              }
            ].map((category, idx) => (
              <Link
                key={idx}
                to={category.link}
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300 bg-white"
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                  />
                </div>
                <div className="p-8">
                  <div className="inline-block px-4 py-1 rounded-full text-sm font-semibold text-white mb-4" style={{ backgroundColor: '#5C3A21' }}>
                    {category.type}
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#5C3A21' }}>
                    {category.title}
                  </h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">{category.description}</p>
                  <p className="text-sm text-gray-600 mb-6">
                    <span className="font-semibold">Matériaux :</span> {category.materials}
                  </p>
                  <div className="flex items-center font-semibold group-hover:translate-x-2 transition-transform duration-300" style={{ color: '#5C3A21' }}>
                    Voir les modèles
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600">
              Découvrez les témoignages de nos clients satisfaits
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8 rounded-2xl bg-gradient-to-br from-[#F5EBDD]/30 to-white border hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
                style={{ borderColor: '#F5EBDD' }}
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5" style={{ fill: '#F5EBDD', color: '#F5EBDD' }} />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover border-2"
                    style={{ borderColor: '#F5EBDD' }}
                  />
                  <div>
                    <p className="font-bold" style={{ color: '#5C3A21' }}>{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-sm text-gray-500">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#F5EBDD' }}></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#E8D5C4' }}></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à investir dans votre machine laser ?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Contactez-nous pour un devis personnalisé ou pour réserver une démonstration.<br />
            Notre équipe d'experts vous accompagne dans votre projet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white font-semibold shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-lg"
              style={{ color: '#5C3A21' }}
            >
              Demander un devis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/machines"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-300 text-lg"
            >
              Voir toutes les machines
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
